import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import type { CreateRecordProperties, DSSetup, RecordData, Service } from 'webapp-types';
import type { CompareFunction, MetricValueType } from 'webapp/enums/enums';
import { BillingFeature, MetricStatus, MetricType, ModelName } from 'webapp/enums/enums';
import { uniq } from 'webapp/utils/common';
import { isNumber } from 'webapp/utils/number';
import type { ValidationRule } from 'webapp/utils/validate';
import { isVerified, type VerificationInfo } from 'webapp/utils/verification';

import type { MetricRecord, TagRecord } from 'webapp/models';
import type { CustomQuery, Goal, Script, Source, SourceType } from 'webapp/models-custom';
import BufferedModel from 'webapp/models-custom/buffer/buffered-model';
import type HorizonMetric from 'webapp/models-custom/horizon-metric';

import type {
	OnAfterMetricEditFn,
	TrackingOriginForMetricEdit,
} from 'webapp/services/model-manager/metrics';

// More info on loading and creating these in model-manager/metrics

export default class Metric extends BufferedModel<MetricRecord, RecordData.Metric.Serialized> {
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;
	@service('model-manager/scripts') declare mmScriptsService: Service.ModelManager.Scripts;
	@service('model-manager/custom-queries')
	declare mmCustomQueriesService: Service.ModelManager.CustomQueries;
	@service('model-manager/goals') declare mmGoalsService: Service.ModelManager.Goals;
	@service('model-manager/horizon-metrics')
	declare mmHorizonMetricsService: Service.ModelManager.HorizonMetrics;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;
	@service('model-manager/tags') declare mmTagsService: Service.ModelManager.Tags;
	@service('billing') declare billingService: Service.Billing;

	constructor(opts: { model: MetricRecord }) {
		super(opts);
		this.isTemporaryDisabledForMetricSelectPopupNotConnected = this.isPredefinedOnGenericSource;
	}

	// ------- props from / to ED record ------

	get id(): string | null {
		return this.model?.id || null;
	}

	get name(): string {
		return this.getBuffered('name');
	}

	set name(value: string) {
		this.setBuffered('name', value);
	}

	get description(): string {
		return this.getBuffered('description');
	}

	set description(value: string) {
		this.setBuffered('description', value);
	}

	get key(): string {
		return this.model.key;
	}

	get objectType(): MetricType {
		return this.model.objectType;
	}

	get verificationInfo(): VerificationInfo | undefined {
		return this.model.verificationInfo;
	}

	get isVerified(): boolean {
		return isVerified(this.verificationInfo);
	}

	get objectId(): number | null {
		return this.model.objectId;
	}

	get dataSourceTypeKey(): string {
		return this.model.dataSourceTypeKey;
	}

	get spaceAccessId(): number | null | undefined {
		return this.getBuffered('spaceAccessId');
	}

	set spaceAccessId(value: number) {
		this.setBuffered('spaceAccessId', value);
	}

	get metricId(): string | null | undefined {
		return this.getBuffered('metricId');
	}

	set metricId(value: string) {
		this.setBuffered('metricId', value);
	}

	get usedCount(): number {
		return this.model.usedCount;
	}

	get isPublic() {
		return this.model.isPublic;
	}

	get status(): MetricStatus {
		return this.model.status;
	}

	get dimensions(): DSSetup.DimensionItem[] {
		return this.getBuffered('dimensions');
	}

	set dimensions(value: DSSetup.DimensionItem[]) {
		// setter used only when creating custom push metric
		this.setBuffered('dimensions', value);
	}

	get hasUnits(): boolean {
		return this.getBuffered('hasUnits');
	}

	set hasUnits(value: boolean) {
		this.setBuffered('hasUnits', value);
	}

	get isStandalone(): boolean {
		return this.model.isStandalone;
	}

	set isStandalone(value: boolean) {
		// setter used only when creating custom push metric
		this.setBuffered('isStandalone', value);
	}

	get noReset(): boolean {
		return this.getBuffered('noReset');
	}

	set noReset(value: boolean) {
		this.setBuffered('noReset', value);
	}

	get format(): string | undefined {
		return this.getBuffered('format');
	}

	set format(value: string) {
		this.setBuffered('format', value);
	}

	get changeFormat(): string | undefined {
		return this.getBuffered('changeFormat');
	}

	set changeFormat(value: string) {
		this.setBuffered('changeFormat', value);
	}

	get changeInvert(): boolean {
		return this.getBuffered('changeInvert');
	}

	set changeInvert(value: boolean) {
		this.setBuffered('changeInvert', value);
	}

	get changeFunction(): CompareFunction {
		return this.getBuffered('changeFunction');
	}

	set changeFunction(value: CompareFunction) {
		this.setBuffered('changeFunction', value);
	}

	get metricType(): string {
		return this.getBuffered('metricType');
	}

	set metricType(value: string) {
		// the metric type properties form keeps it as a string
		this.setBuffered('metricType', value as MetricValueType);
	}

	get aggregationFunction(): string | null {
		return this.getBuffered('aggregationFunction');
	}

	set aggregationFunction(value: string | null) {
		this.setBuffered('aggregationFunction', value);
	}

	get granulationOrigin(): number | null {
		return this.getBuffered('granulationOrigin');
	}

	set granulationOrigin(value: number | null) {
		this.setBuffered('granulationOrigin', value);
	}

	get dimension(): string | null {
		return this.model.dimension;
	}

	get hasFutureData(): boolean {
		return this.getBuffered('hasFutureData');
	}

	set hasFutureData(value: boolean) {
		this.setBuffered('hasFutureData', value);
	}

	get isHistoryChanging(): boolean {
		return this.getBuffered('isHistoryChanging');
	}

	set isHistoryChanging(value: boolean) {
		this.setBuffered('isHistoryChanging', value);
	}

	get ensureUnique(): boolean {
		return this.getBuffered('ensureUnique');
	}

	set ensureUnique(value: boolean) {
		this.setBuffered('ensureUnique', value);
	}

	get isAppend(): boolean {
		return this.getBuffered('isAppend');
	}

	set isAppend(value: boolean) {
		this.setBuffered('isAppend', value);
	}

	get isHistoricalDataPersistenceEnabled(): boolean | null {
		return this.getBuffered('isHistoricalDataPersistenceEnabled');
	}

	set isHistoricalDataPersistenceEnabled(value: boolean | null) {
		this.setBuffered('isHistoricalDataPersistenceEnabled', value);
	}

	get hasMedia(): boolean {
		return this.model.hasMedia;
	}

	get cloneFrom(): string | null {
		return this.model.cloneFrom;
	}

	// ------- other props ------

	override get validationRules(): Record<string, ValidationRule[]> {
		return {
			name: ['required'],
		};
	}

	get isTypeBasic(): boolean {
		return this.model.isTypeBasic;
	}

	get isTypeMetricCalculation(): boolean {
		return this.model.isTypeMetricCalculation;
	}

	get isTypeCustomQuery(): boolean {
		return this.model.isTypeCustomQuery;
	}

	get isTypeCustomPush(): boolean {
		return this.model.isTypeCustomPush;
	}

	get isTypeGoal(): boolean {
		return this.model.isTypeGoal;
	}

	get isTypeHorizon(): boolean {
		return this.model.isTypeHorizon;
	}

	get isTypeBuilt(): boolean {
		return this.model.isTypeBuilt;
	}

	get isTypeCustomQueryGeneric(): boolean {
		return this.model.isTypeCustomQueryGeneric;
	}

	get isTypeMetricCalculationGeneric(): boolean {
		return this.model.isTypeMetricCalculationGeneric;
	}

	get isTypeCustomQueryOrGeneric(): boolean {
		return this.model.isTypeCustomQueryOrGeneric;
	}

	get isTypeMetricCalculationOrGeneric(): boolean {
		return this.model.isTypeMetricCalculationOrGeneric;
	}

	get isOnSource() {
		return this.model.isOnSource;
	}

	get isGenericBasic() {
		// cloud source predefined metrics (only on generic source)
		return this.model.isGenericBasic;
	}

	get isGenericCustom() {
		// generic script / cq (also from predefined space)
		return this.model.isGenericCustom;
	}

	get isGeneric() {
		// all generics on all sources
		return this.model.isGeneric;
	}

	get isPredefined() {
		// generic public predefined cq / scripts (and its copies on sources)
		return this.model.isPredefined;
	}

	get isUserGeneric() {
		// user's generic cq / scripts (not predefined)
		return this.model.isUserGeneric;
	}

	get isPredefinedOnGenericSource() {
		// generic public redefined cq / scripts (only on generic source)
		return this.model.isPredefinedOnGenericSource;
	}

	get allowDimensions(): boolean {
		// just means that we can select a dimension, not that we have to
		// (e.g. Sessions is standalone, but has dimensions)
		return this.dimensions.length > 0;
	}

	get isLegacyCustom(): boolean {
		return this.sourceType.isLegacyCustomSourceType && !this.source?.isTypeOfDataset;
	}

	get dimensionsAvailableKeys(): string[] {
		return this.dimensions.map((d) => d.key);
	}

	get dimensionsAvailableValues(): string[] {
		return this.dimensions.map((d) => d.value);
	}

	@cached
	get dimensionsString(): string {
		return this.dimensionsAvailableValues.join(', ');
	}

	get mustSelectDimension(): boolean {
		return !this.isStandalone && this.allowDimensions;
	}

	get autoselectedDimension(): string | null {
		// only if !isStandalone with one dimension (case for custom queries, scripts, pushable, ...)
		if (this.mustSelectDimension && this.dimensions.length === 1) {
			return this.dimensions[0]!.key;
		}
		return null;
	}

	get isDeprecated(): boolean {
		return this.model.status === MetricStatus.DISABLED;
	}

	get hasPercentFormat(): boolean {
		const format = this.format || '';

		return format.includes('%') || format.includes('◊');
	}

	get defaults() {
		return this.mmMetricsService.getDefaultsForMetric(this);
	}

	get metricData(): DSSetup.MetricData | null {
		return this.model.metricData;
	}

	get metricSetupData(): DSSetup.MetricSetupData | null {
		// is different from metricData only when autoselectedDimension is available
		const metricData = this.metricData;

		if (metricData) {
			const autoselectedDimension = this.autoselectedDimension;
			if (autoselectedDimension) {
				return Object.assign(
					{
						dimension: autoselectedDimension,
					},
					metricData,
				);
			}
		}
		return metricData;
	}

	@cached
	get sourceType(): SourceType {
		if (this.dataSourceTypeKey === 'FromUnitTest') {
			return {} as SourceType; // a stub for unit tests
		} else {
			return this.mmSourceTypesService.findModelByKey(this.dataSourceTypeKey)!;
		}
	}

	@cached
	get source(): Source | null {
		// null only for generics or client metrics (from other spaces)
		let source = null;
		if (this.spaceAccessId) {
			source = this.mmSourcesService.findModel(String(this.spaceAccessId));
		}
		return source;
	}

	@cached
	get siblingMetrics() {
		if (this.isTypeMetricCalculation && this.objectId) {
			return this.mmMetricsService.metrics.filter((m) => {
				return (
					m.isTypeMetricCalculation && m.objectId === this.objectId && m.id !== this.id
				);
			});
		}
		return [];
	}

	@cached
	get siblingSources() {
		const sources = new Set<Source>();
		this.siblingMetrics.forEach((m) => {
			if (m.source) {
				sources.add(m.source);
			}
		});
		if (this.source) {
			sources.delete(this.source);
		}
		return Array.from(sources);
	}

	@cached
	get sourcesText() {
		const texts = [this.sourceName];
		this.siblingMetrics.forEach((m) => {
			texts.push(m.sourceName);
		});
		return uniq(texts).join(', ');
	}

	@cached
	get rootModel(): Script | CustomQuery | Goal | HorizonMetric | null {
		let rootModel = null;

		const objectId = this.objectId;
		if (objectId && !this.isPublic) {
			const stringObjectId = String(objectId);
			if (this.isTypeMetricCalculation) {
				rootModel = this.mmScriptsService.findModel(stringObjectId);
			} else if (this.isTypeCustomQuery) {
				rootModel = this.mmCustomQueriesService.findModel(stringObjectId);
			} else if (this.isTypeGoal) {
				rootModel = this.mmGoalsService.findModel(stringObjectId);
			} else if (this.isTypeHorizon) {
				rootModel = this.mmHorizonMetricsService.findModel(stringObjectId);
			}
		}

		return rootModel;
	}

	get rootModelOriginateFrom() {
		if (this.isTypeCustomQuery || this.isTypeMetricCalculation) {
			return (this.rootModel as Script | CustomQuery)?.originateFrom;
		}
		return null;
	}

	// -------- Edit ---------

	get canEdit() {
		return this.isTypeCustomPush || !!this.rootModel?.canEdit;
	}

	get isFromTokenAndNotEditable(): boolean {
		return !!this.sourceType?.isToken && !this.billingService.dataPushEnabled;
	}

	get canEditDefinition(): boolean {
		if (this.isTypeGoal || this.isFromTokenAndNotEditable) {
			return false;
		}

		return this.isTypeBasic || this.canEdit;
	}

	get editMetricWillOpenQb(): boolean {
		return this.isTypeCustomQuery;
	}

	edit(
		origin: TrackingOriginForMetricEdit = '',
		afterSave?: OnAfterMetricEditFn,
		extraProps: Parameters<Service.ModelManager.Metrics['editMetric']>[3] = {},
	) {
		return this.mmMetricsService.editMetric(this, origin, afterSave, extraProps);
	}

	override getDataForCopy(): CreateRecordProperties<MetricRecord> {
		const data = this.toJSON();

		data.cloneFrom = this.id;
		data.name = `Copy of ${data.name}`;

		return data;
	}

	// ----- User metric created from predefined metric -----

	@cached
	get createdFromPredefinedMetric(): Metric | null {
		const originateFrom = this.rootModelOriginateFrom;
		if (originateFrom) {
			return (
				this.mmMetricsService.genericPredefinedMetrics.find((m) => {
					return (
						m.dataSourceTypeKey === this.dataSourceTypeKey &&
						m.objectId === originateFrom
					);
				}) || null
			);
		}
		return null;
	}

	get isCreatedFromPredefinedMetric() {
		return !!this.createdFromPredefinedMetric;
	}

	// ----- "Hacks" for metric select popup -----

	// set and reset in metric-select/popup/index (youtube, generic -- dependent on metric-select options)
	@tracked isTemporaryDisabledForMetricSelectPopupYoutube = false;
	@tracked isTemporaryDisabledForMetricSelectPopupNotConnected = false;

	get isTemporaryDisabledForMetricSelectPopup() {
		return (
			this.isTemporaryDisabledForMetricSelectPopupYoutube ||
			this.isTemporaryDisabledForMetricSelectPopupNotConnected
		);
	}

	// ----- Billing limitations -----

	get missingBillingFeature() {
		let limitation;
		if (this.isTypeCustomQueryOrGeneric && !this.billingService.checkIfCan(BillingFeature.QB)) {
			limitation = BillingFeature.QB;
		} else if (
			this.isTypeMetricCalculationOrGeneric &&
			!this.billingService.checkIfCan(BillingFeature.SCRIPTS)
		) {
			limitation = BillingFeature.SCRIPTS;
		}
		return limitation;
	}

	get showQbWarning() {
		return this.missingBillingFeature === BillingFeature.QB;
	}

	get showScriptWarning() {
		return this.missingBillingFeature === BillingFeature.SCRIPTS;
	}

	get isUsageDisabled() {
		return !!this.missingBillingFeature || !!this.source?.isStatusActionable;
	}

	handleQbOrScriptWarning() {
		const feature = this.missingBillingFeature;
		if (feature) {
			this.billingService.handleLimitations(feature);
			return true;
		}
		return false;
	}

	// ------ QoL stuff ------

	@cached // cached bcos used in metric-sort fn
	get sourceName(): string {
		return this.source?.title || this.sourceType?.name || '';
	}

	get sourceAvatar(): string {
		return this.source?.realAvatar || this.sourceType?.avatar || '';
	}

	// Favorites move onto the metric registry item in M2; until then nothing is favorited.
	get isFavorite(): boolean {
		return false;
	}

	@cached
	get tagRecords(): TagRecord[] {
		const tagIds = this.rootModel?.tags || [];

		return this.mmTagsService.savedRecords.filter((tag) => {
			return tagIds.includes(+tag.id!);
		});
	}

	get mustResolveBeforeSelecting() {
		return this.isPredefined;
	}

	@cached
	get allowAdvancedSetup() {
		// allows multi dimensions and advanced filtering
		return (
			this.isTypeHorizon || (!this.isTypeMetricCalculation && !!this.source?.isTypeOfDataset)
		);
	}

	@cached
	get allowAdvancedFilters() {
		return this.allowAdvancedSetup;
	}

	isFromSourceListingValue(value: string) {
		// value is either stringified spaceAccessId or sourceTypeKey
		if (isNumber(value)) {
			return String(this.spaceAccessId) === value;
		} else {
			return value === 'Excel'
				? this.dataSourceTypeKey.includes('Excel')
				: this.dataSourceTypeKey === value;
		}
	}

	cleanupLimitationsCache() {
		const key = JSON.stringify(this.metricData);
		this.mmMetricsService.cleanupLimitationsCacheForKey(key);
		this.mmMetricsService.cleanupLimitationsCacheForKey(`${key}/script`);
	}

	// ----------- Static -----------

	static override modelName = ModelName.METRIC; // store record type
}
